import type { ExtractionStatus } from '@vrzl/database';
import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
} from '@vrzl/ui';
import { useAtom } from 'jotai';
import { ClipboardCheck, Clock, Download, ListChecks, ShieldOff, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useAuth } from '@/domains/auth';
import {
  countActiveCriteria,
  selectActiveCriterionIds,
  useCriteria,
  CriteriaPanel,
} from '@/domains/criteria';
import { useDocuments } from '@/domains/documents';
import { EventHeader, useEvent } from '@/domains/events';

import { useValidateCriteria } from '../api/mutations';
import { useLatestExtraction, useValidationReport } from '../api/queries';
import { printModeAtom } from '../state/printMode.atom';
import { validationReportAtom } from '../state/validationReport.atom';
import { matchStoredReport } from '../state/validationReportMatch';

import { ValidationResult } from './validationResult';

function emptyStateContent(
  status: ExtractionStatus | null,
): { icon: LucideIcon; title: string; description: string } {
  switch (status) {
    case 'QUEUED':
      return {
        icon: Clock,
        title: 'Extractie staat in de wachtrij',
        description: 'Validatie kan starten zodra de extractie is voltooid.',
      };
    case 'IN_PROGRESS':
      return {
        icon: Clock,
        title: 'Extractie loopt nog',
        description: 'Validatie kan starten zodra de extractie is voltooid.',
      };
    case 'COMPLETED':
      return {
        icon: ClipboardCheck,
        title: 'Geen criteria gekoppeld',
        description: 'Er zijn nog geen criteria gekoppeld aan dit perspectief om te valideren.',
      };
    case 'FAILED':
      return {
        icon: TriangleAlert,
        title: 'Extractie mislukt',
        description: 'Validatie is niet mogelijk omdat de extractie van de documenten is mislukt.',
      };
    case null:
      return {
        icon: Clock,
        title: 'Nog geen extractie beschikbaar',
        description: 'Zodra de documenten zijn uitgelezen kun je een validatie starten.',
      };
  }
}

export function Report() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const activePerspective = user?.activePerspective ?? null;
  const { data: event } = useEvent(eventId ?? '');
  // Scope criteria (count + validatie) op het actieve perspectief van de gebruiker.
  const { data: criteriaData } = useCriteria(event?.applicationId ?? '', {
    perspectiveId: activePerspective?.id,
  });
  const { data: extractionData } = useLatestExtraction(eventId ?? '');
  const { data: documentsData } = useDocuments(eventId ?? '');

  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [inactiveCriterionIds, setInactiveCriterionIds] = useState<Set<string>>(new Set());

  const { mutate, isPending, error } = useValidateCriteria();
  const [storedReport, setStoredReport] = useAtom(validationReportAtom);

  const [printMode, setPrintMode] = useAtom(printModeAtom);

  useEffect(() => {
    if (!printMode) return;
    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);
    window.print();
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      setPrintMode(false);
    };
  }, [printMode, setPrintMode]);

  const latestExtraction = extractionData?.items[0] ?? null;
  const extractionStatus = latestExtraction?.status ?? null;
  const extractionId = extractionStatus === 'COMPLETED' ? latestExtraction?.id ?? null : null;
  // Tellers uit de geladen criteria, niet uit het server-totaal: alleen deze lijst
  // gaat ook echt naar /criteria/validate.
  const criteria = criteriaData?.items ?? [];
  const documents = documentsData?.items ?? [];

  // Alle reeds gekoppelde criteria, ongefilterd door de inactive-toggles: die
  // zijn bij het laden altijd leeg en worden pas relevant in het criteriapaneel.
  const criterionIds = useMemo(
    () => (criteriaData?.items ?? []).map((c) => c.id),
    [criteriaData],
  );
  const autoValidation = useValidationReport(extractionId, criterionIds);

  if (!activePerspective) {
    return (
      <div>
        <PageHeader
          title="Documentvalidatie"
          description="Valideer de documenten om de inhoud te controleren tegen de criteria"
        />
        <EmptyState
          icon={ShieldOff}
          title="Geen perspectief toegewezen"
          description="Je hebt geen actief perspectief. Neem contact op met je beheerder."
        />
      </div>
    );
  }

  const totalCriteria = criteria.length;
  const activeCriteria = countActiveCriteria(criteria, inactiveCriterionIds);
  const canValidate = extractionStatus === 'COMPLETED';
  const extractionErrors = extractionStatus === 'FAILED' ? latestExtraction?.errors : null;

  const perspectiveId = activePerspective.id;
  const manualReport = matchStoredReport(storedReport, extractionId, perspectiveId);
  const report = manualReport ?? autoValidation.data ?? null;
  const validationError = error ?? autoValidation.error;
  const isAutoValidating = !report && autoValidation.isLoading;

  function toggleCriterion(id: string): void {
    setInactiveCriterionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleValidate(): void {
    if (!extractionId) return;
    const criterionIds = selectActiveCriterionIds(criteria, inactiveCriterionIds);
    // Bewaren met het perspectief van deze render, niet dat van later.
    mutate(
      { criterionIds, extractionId },
      { onSuccess: (report) => setStoredReport({ perspectiveId, report }) },
    );
    setCriteriaOpen(false);
  }

  return (
    <div>
      {event && (
        <EventHeader
          event={event}
          documents={documents}
          actions={
            <div className="flex items-center gap-3 print:hidden">
              {report && (
                <VButton variant="outline" icon={Download} onClick={() => setPrintMode(true)}>
                  Exporteer als PDF
                </VButton>
              )}
              <VButton
                variant="outline"
                icon={ListChecks}
                disabled={!canValidate}
                onClick={() => setCriteriaOpen(true)}
              >
                criteria aanpassen
              </VButton>
            </div>
          }
        />
      )}

      {report ? (
        <ValidationResult
          report={report}
          eventId={eventId ?? ''}
          activeCriteria={activeCriteria}
          totalCriteria={totalCriteria}
        />
      ) : isAutoValidating ? (
        <LoadingState message="Validatie wordt uitgevoerd..." />
      ) : validationError ? (
        <ErrorMessage error={validationError} message="Validatie mislukt. Probeer het opnieuw." />
      ) : extractionStatus === 'FAILED' ? (
        <ErrorMessage
          error={new Error('Extraction failed')}
          message={
            extractionErrors
              ? JSON.stringify(extractionErrors)
              : 'Er kon geen validatierapport worden gemaakt: er is geen extractiedata gevonden voor dit evenement.'
          }
        />
      ) : (
        <EmptyState {...emptyStateContent(extractionStatus)} />
      )}

      {event && (
        <CriteriaPanel
          applicationId={event.applicationId}
          perspective={activePerspective}
          open={criteriaOpen}
          onOpenChange={setCriteriaOpen}
          inactiveCriterionIds={inactiveCriterionIds}
          onToggleCriterion={toggleCriterion}
          onValidate={handleValidate}
          isValidating={isPending}
        />
      )}
    </div>
  );
}
